/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import type {
	SpotlightProps as DeprecatedSpotlightProps,
	Actions,
} from '@atlaskit/onboarding/types';
import { Text } from '@atlaskit/primitives/compiled';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { PopoverContent, type PopoverContentProps } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';
import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import type {
	ChoreographedComponentProps,
	ChoreographerMessageTypes,
	ChoreographerUtilities,
} from '../../types';
import { ChoreographedComponent } from '../choreographed-component';
import { actions as actionsTranslation } from './translations/actions';
import { children as childrenTranslation } from './translations/children';
import { dialogPlacement as dialogPlacementTranslation } from './translations/dialog-placement';
import { heading as headingTranslation } from './translations/headings';
import { target as targetTranslation } from './translations/target';
import { useMountToHtmlElement } from './useMountToHtmlElement';

const styles = cssMap({
	imageWrapper: {
		width: '100%',
	},
});

const translations = {
	actions: actionsTranslation,
	heading: headingTranslation,
	children: childrenTranslation,
	dialogPlacement: dialogPlacementTranslation,
	target: targetTranslation,
};

type Action = Actions[number] & { ctaType?: 'primary' | 'secondary' };

export type MigratedSpotlightProps = DeprecatedSpotlightProps &
	ChoreographerUtilities &
	Partial<ChoreographedComponentProps> & {
		isMigrated?: boolean;
		messageType?: ChoreographerMessageTypes;
		offset?: PopoverContentProps['offset'];
		actions?: Action[];
	};

export const MigratedSpotlight = (props: MigratedSpotlightProps): React.ReactNode => {
	const {
		messageId,
		messageType,
		onMessageDisposition,
		shouldBeChoreographed,
		withChoreographedRender,
		...rest
	} = props;
	const placement = translations.dialogPlacement(
		rest.dialogPlacement,
	) as PopoverContentProps['placement'];
	const actions = translations.actions(rest.actions);
	const heading = translations.heading(rest.heading);
	const body = translations.children(rest.children);
	const id = translations.target(rest.target);

	const [isVisible, setIsVisible] = useState(true);

	const dismiss = useCallback(() => {
		setIsVisible(false);
	}, []);

	const renderInline = useMountToHtmlElement({
		selectors: id ? `#${id}` : undefined,
		as: 'inline',
		position: 'inside',
	});

	return renderInline(
		<ChoreographedComponent
			messageId={messageId}
			messageType={messageType}
			onMessageDisposition={onMessageDisposition}
			shouldBeChoreographed={shouldBeChoreographed}
			withChoreographedRender={withChoreographedRender}
		>
			<PopoverProvider>
				<PopoverTarget>
					<div />
				</PopoverTarget>
				<PopoverContent
					offset={rest.offset}
					dismiss={dismiss}
					placement={placement}
					isVisible={isVisible}
				>
					<SpotlightCard>
						<SpotlightHeader>
							<SpotlightHeadline>{heading}</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl onClick={dismiss} />
							</SpotlightControls>
						</SpotlightHeader>
						{rest.image && (
							<SpotlightMedia>
								<div css={styles.imageWrapper}>
									<Image src={rest.image} alt="" />
								</div>
							</SpotlightMedia>
						)}
						<SpotlightBody>
							<Text>{body}</Text>
						</SpotlightBody>
						<SpotlightFooter>
							{rest.actionsBeforeElement && (
								<SpotlightStepCount>{rest.actionsBeforeElement}</SpotlightStepCount>
							)}
							<SpotlightActions>
								{actions.secondary && (
									<SpotlightSecondaryAction onClick={actions.secondary.onClick}>
										{actions.secondary.text}
									</SpotlightSecondaryAction>
								)}
								{actions.primary && (
									<SpotlightPrimaryAction onClick={actions.primary.onClick}>
										{actions.primary.text}
									</SpotlightPrimaryAction>
								)}
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		</ChoreographedComponent>,
	);
};
